import React from 'react';
import type { Table, Order } from '../../types';
import { isActiveBuffetSession, isFloorPlanActiveTableOrder } from '../../lib/orderUtils';

interface TableSplitModalProps {
  mergedTable: Table;
  originalTables: Table[];
  orders: Order[];
  onConfirm: () => void;
  onCancel: () => void;
}

const TableSplitModal: React.FC<TableSplitModalProps> = ({
  mergedTable,
  originalTables,
  orders,
  onConfirm,
  onCancel,
}) => {
  const primaryTableId = mergedTable.mergedWith?.[0] ?? mergedTable.id;
  const primaryNum = Number(primaryTableId);

  const hasActiveOrder = orders.some(order => {
    if (isActiveBuffetSession(order) && isFloorPlanActiveTableOrder(order, primaryNum)) return true;
    if (!isFloorPlanActiveTableOrder(order, primaryNum)) return false;
    return order.orderType === 'dine-in';
  });

  const canSplit = !hasActiveOrder;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-60 z-50 flex justify-center items-center p-4">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-md">
        <header className="p-4 border-b">
          <h2 className="text-xl font-bold text-gray-800">Split Merged Table</h2>
        </header>
        <main className="p-4">
          <div className="mb-4">
            <p className="text-sm text-gray-600 mb-3">Current merged table:</p>
            <div className="p-3 bg-gray-50 rounded border mb-4">
              <p className="font-semibold">{mergedTable.name}</p>
              <p className="text-sm text-gray-500">Capacity: {mergedTable.capacity}</p>
            </div>
          </div>

          {!canSplit && (
            <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded">
              <p className="text-sm text-red-700 font-semibold">
              Cannot split: this merged group still has an active dine-in or buffet order for today. Finish or cancel it first.
              </p>
            </div>
          )}

          {canSplit && (
            <div className="mb-4">
              <p className="text-sm text-gray-600 mb-3">Tables that will be restored:</p>
              <div className="space-y-2">
                {originalTables.map(table => (
                  <div
                    key={table.id}
                    className="p-2 bg-blue-50 rounded border border-blue-200"
                  >
                    <p className="font-semibold text-blue-900">{table.originalName ?? table.name}</p>
                    <p className="text-xs text-blue-700">
                      Capacity: {table.originalCapacity ?? table.capacity}
                    </p>
                  </div>
                ))}
              </div>
            </div>
          )}
        </main>
        <footer className="p-4 border-t flex justify-end gap-2">
          <button
            onClick={onCancel}
            className="px-4 py-2 text-gray-700 bg-gray-100 rounded hover:bg-gray-200 transition-colors"
          >
            Cancel
          </button>
          <button
            onClick={onConfirm}
            disabled={!canSplit}
            className={`px-4 py-2 text-white rounded transition-colors ${
              canSplit
                ? 'bg-blue-500 hover:bg-blue-600'
                : 'bg-gray-300 cursor-not-allowed'
            }`}
          >
            Split Tables
          </button>
        </footer>
      </div>
    </div>
  );
};

export default TableSplitModal;

